import { useEffect, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, InputBase, Typography, styled } from '@mui/material';

import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { openRegister, openForgot } from '../../../../redux/slices/authUiSlice';

import MyDialog from '../../../../components/dialog-component/my-dialog';
import Image from '../../../../components/Image';

import visibilityOn from '../../../../assets/home/visibility-on.png';
import visibilityOff from '../../../../assets/home/visibility-off.png';

import useAuth from '../../../../hooks/useAuth';
import useLocales from '../../../../hooks/useLocales';
import { validatePhoneNumber } from '../../../../utils/validatePhoneNumber';
import Logo from '../../../../assets/brand-logo.png';
import ForgotPassword from '../../../../pages/auth/ForgotPassword';
import Register from './Register';

// ===== Styled Components =====
const StyledInput = styled(InputBase)(() => ({
  fontWeight: 500,
  fontFamily: 'sans-serif',
  background: 'rgba(255, 255, 255, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  borderRadius: '10px',
  color: '#fff',
  padding: '10px 14px',
  fontSize: '15px',
  '& input::placeholder': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
}));

// ===== Login Form Default =====
const _loginForm = {
  userUsername: '',
  userPassword: '',
};

const LoginForm = ({ handleClose, handleOpen }) => {
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const dispatchUi = useDispatch();

  const [loginForm, setLoginForm] = useState(_loginForm);
  const [formError, setFormError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  // ===== Handle Form Change =====
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  // ===== Validation (from goodlogin) =====
  // const isValid = () => {
  //   let valid = true;
  //   const _error = {};
  //   setFormError(_error);
  //   if (!loginForm?.userUsername?.trim()) {
  //     valid = false;
  //     _error.userUsername = translate('invalid_x', { x: translate('phone_number') });
  //   } else if (loginForm?.userUsername?.trim()?.length !== 9) {
  //     valid = false;
  //     _error.userUsername = translate('minimum_length_is_x', { x: 9 });
  //     _error.userUsername += ', ';
  //     _error.userUsername += translate('maximum_length_is_x', { x: 9 });
  //   } else if (!/^[35789]/.test(loginForm?.userUsername?.trim())) {
  //     valid = false;
  //     _error.userUsername = translate('username_must_start_with', { x: '3, 5, 7, 8, or 9' });
  //   }

  //   // else {
  //   //   const invalidPhone = validatePhoneNumber(loginForm.userUsername.trim(), translate);
  //   //   if (invalidPhone) {
  //   //     valid = false;
  //   //     _error.userUsername = invalidPhone;
  //   //   }
  //   // }

  //   if (loginForm?.userPassword?.trim()?.length < 6 || loginForm?.userPassword?.trim()?.length > 20) {
  //     valid = false;
  //     _error.userPassword = translate('password_must_be_6_to_20');
  //   }
  //   setFormError(_error);
  //   return valid;
  // };

  const isValid = () => {
    let valid = true;

    const _error = {};
    setFormError(_error);

    const username = loginForm?.userUsername?.trim();
    const password = loginForm?.userPassword?.trim();

    // Phone number validation
    if (!username) {
      valid = false;
      _error.userUsername = translate('invalid_x', { x: translate('phone_number') });
    } else if (!/^[3-9]\d{8}$/.test(username)) {
      valid = false;
      _error.userUsername = translate('phone_number_must_start_with', { x: '3, 5, 7, 8, or 9' });
    }

    // Password validation
    if (!password || password.length < 6 || password.length > 20) {
      valid = false;
      _error.userPassword = translate('password_must_be_6_to_20');
    }

    setFormError(_error);
    return valid;
  };

  // ===== Submit =====
  const onSubmit = async (event) => {
    event?.preventDefault();

    // if (!isValid()) return;

    setIsLoading(true);

    const requestData = {
      'user-username': loginForm.userUsername.trim(),
      'user-password': loginForm.userPassword.trim(),
    };

    try {
      await login(requestData);
      window.parent.location = '/';
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Press Enter to login =====
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === 'Enter') {
        onSubmit();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => document.removeEventListener('keydown', keyDownHandler);
  }, [loginForm]);

  return (
    <>
      <MyDialog open={handleOpen} close={handleClose} maxSize={'sm'}>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, mb: 3 }}>
            <Image sx={{ width: 180 }} src={Logo} />
          </Box>

          <form onSubmit={onSubmit} style={{ width: '100%' }}>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {/* Username / Phone */}
              <StyledInput
                fullWidth
                name="userUsername"
                value={loginForm.userUsername}
                placeholder="e.g. 912345678"
                onChange={handleFormChange}
                startAdornment={
                  <InputAdornment position="start">
                    <Image
                      sx={{ width: 25, height: 25 }}
                      src="https://a865c0.swuacktu.net/system-requirement/Web.PortalNew/UK256-01/e9b1065239/images/0a4b0ace31dd95018ec5ab31eaa5270a.png"
                    />
                  </InputAdornment>
                }
              />
              {formError?.userUsername && (
                <Typography sx={{ color: 'red', fontSize: 12 }}>{formError.userUsername}</Typography>
              )}

              {/* Password */}
              <StyledInput
                fullWidth
                placeholder={translate('password_placeholder')}
                type={showPassword ? 'text' : 'password'}
                name="userPassword"
                value={loginForm.userPassword}
                onChange={handleFormChange}
                startAdornment={
                  <InputAdornment position="start">
                    <Image
                      sx={{ width: 25, height: 25 }}
                      src="https://a865c0.swuacktu.net/system-requirement/Web.PortalNew/UK256-01/e9b1065239/images/6759267d3ba19d37147b7817f2e1476b.png"
                    />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPassword} edge="end">
                      {showPassword ? (
                        <Image src={visibilityOn} sx={{ width: 25, height: 25 }} />
                      ) : (
                        <Image src={visibilityOff} sx={{ width: 25, height: 25 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {formError?.userPassword && (
                <Typography sx={{ color: 'red', fontSize: 12 }}>{formError.userPassword}</Typography>
              )}
            </Box>

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              disabled={isLoading}
              sx={{
                mt: 4,
                mb: 2,
                background: 'linear-gradient(to right, #c34029, #ff7214)',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '16px',
                borderRadius: '8px',
              }}
            >
              {isLoading ? translate('logging_in') : translate('login')}
            </Button>
          </form>
          <Box display={'flex'} gap={1} justifyContent={'center'} alignContent={'center'} mb={2}>
            <Typography
              sx={{
                fontSize: { xs: '0.9rem', sm: '0.875rem', md: '1rem' }, // responsive font
              }}
              textAlign={'center'}
            >
              {translate('dont_have_account')}
            </Typography>
            <Typography
              onClick={() => dispatchUi(openRegister())}
              sx={{
                cursor: 'pointer',
                fontSize: { xs: '0.9rem', sm: '0.875rem', md: '1rem' },
              }}
              textAlign={'center'}
              fontWeight={'bold'}
              color={'primary'}
            >
              {translate('register')}
            </Typography>
          </Box>
          <Typography
            textAlign="center"
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={() => dispatchUi(openForgot())}
          >
            {translate('forgot_password')} ?
          </Typography>
        </Box>
      </MyDialog>
    </>
  );
};

export default LoginForm;
